import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { pollEvery } from '../realtime/live';

/* ------------------------------------------------------------------- types */
export type Station = 'KITCHEN' | 'BAR';
export type LineStatus = 'NEW' | 'QUEUED' | 'ACCEPTED' | 'READY' | 'SERVED' | 'REJECTED' | 'VOID';
export type Tender = 'CASH' | 'MOMO' | 'CARD' | 'ROOM';

export interface MenuVariant {
  id: string;
  name: string;
  price: number;
  active: boolean;
  /** false when an ingredient it needs has run out */
  inStock: boolean;
  hasRecipe: boolean;
}
export interface Modifier {
  id: string;
  name: string;
  price: number;
  active: boolean;
}
export interface ModifierGroup {
  id: string;
  name: string;
  minChoices: number;
  maxChoices: number;
  modifiers: Modifier[];
}
export interface ChosenModifier {
  group: string;
  name: string;
  price: number;
}
export interface MenuCombo {
  id: string;
  name: string;
  price: number;
  parts: string[];
  available: boolean;
}
export interface MenuItem {
  id: string;
  name: string;
  description: string | null;
  station: Station;
  department: 'FOOD' | 'BEVERAGE';
  prepMinutes: number | null;
  active: boolean;
  soldOut: boolean;
  /** null when the item has no picture; changes with every upload */
  photoVersion: number | null;
  variants: MenuVariant[];
  modifierGroups: ModifierGroup[];
}
export interface MenuCategory {
  id: string;
  name: string;
  active: boolean;
  items: MenuItem[];
}

export interface DiningTable {
  id: string;
  name: string;
  area: string | null;
  capacity: number;
  openOrder: { id: string; reference: string; covers: number; total: number; openedAt: string } | null;
}

export interface OrderLine {
  id: string;
  itemName: string;
  variantName: string;
  department: string;
  station: Station;
  unitPrice: number;
  qty: number;
  total: number;
  note: string | null;
  modifiers: ChosenModifier[];
  comboGroupId: string | null;
  comboName: string | null;
  status: LineStatus;
  billId: string | null;
  reason: string | null;
  sentAt: string | null;
  readyAt: string | null;
}

export interface BillDepartment {
  department: string;
  gross: number;
  net: number;
  taxes: { code: string; name: string; rateBps: number; amount: number }[];
}
export interface Bill {
  id: string;
  number: string;
  status: 'OPEN' | 'PAID' | 'VOID' | 'REFUNDED';
  subtotal: number;
  discount: number;
  discountReason: string | null;
  total: number;
  paid: number;
  due: number;
  breakdown: BillDepartment[];
  taxes: { code: string; name: string; rateBps: number; amount: number }[];
  bookingId: string | null;
  createdAt: string;
  paidAt: string | null;
  payments: { id: string; tender: Tender; amount: number; reference: string | null; createdAt: string }[];
}

export interface Order {
  id: string;
  reference: string;
  target: 'TABLE' | 'ROOM_STAY' | 'TAKEAWAY';
  status: 'OPEN' | 'PAID' | 'CANCELLED';
  table: { id: string; name: string } | null;
  bookingId: string | null;
  label: string | null;
  covers: number;
  waiter: string;
  openedAt: string;
  total: number;
  unsentCount: number;
  lines: OrderLine[];
  bills: Bill[];
}
export type OrderSummary = Omit<Order, 'lines' | 'bills'> & { billCount: number };

export interface QueueLine {
  id: string;
  orderId: string;
  orderRef: string;
  where: string;
  covers: number;
  itemName: string;
  variantName: string;
  qty: number;
  note: string | null;
  modifiers: ChosenModifier[];
  comboName: string | null;
  station: Station;
  status: LineStatus;
  sentAt: string;
  readyAt: string | null;
}

export interface Shift {
  id: string;
  status: 'OPEN' | 'CLOSED';
  cashier?: string;
  openedAt: string;
  closedAt: string | null;
  openingFloat: number;
  byTender: Partial<Record<Tender, { amount: number; count: number }>>;
  cashTaken: number;
  cashRefunded: number;
  expectedCash: number | null;
  countedCash: number | null;
  variance: number | null;
  note: string | null;
}

export interface InHouseGuest {
  bookingId: string;
  reference: string;
  room: string | null;
  guest: string;
  checkOut?: string;
}

/* ------------------------------------------------------------------- queries */
const fetchMenu = (all: boolean) => api<{ categories: MenuCategory[]; combos: MenuCombo[] }>(`/menu${all ? '?all=1' : ''}`);
export const useMenu = (all = false) => useQuery({ queryKey: ['menu', all], queryFn: () => fetchMenu(all), select: (r) => r.categories });
/** Set menus, from the same request as the menu. */
export const useMenuCombos = () => useQuery({ queryKey: ['menu', false], queryFn: () => fetchMenu(false), select: (r) => r.combos });
export const useTables = () => useQuery({ queryKey: ['rs', 'tables'], refetchInterval: pollEvery(15_000), queryFn: () => api<{ tables: DiningTable[] }>('/tables').then((r) => r.tables) });
export const useOpenOrders = () => useQuery({ queryKey: ['rs', 'orders'], refetchInterval: pollEvery(15_000), queryFn: () => api<{ orders: OrderSummary[] }>('/orders?status=OPEN').then((r) => r.orders) });
export const useOrder = (id: string) => useQuery({ queryKey: ['rs', 'order', id], refetchInterval: pollEvery(10_000), queryFn: () => api<Order>(`/orders/${id}`) });
export const useQueue = (station: Station) => useQuery({ queryKey: ['rs', 'queue', station], refetchInterval: pollEvery(5_000), refetchIntervalInBackground: true, queryFn: () => api<{ lines: QueueLine[] }>(`/stations/${station}/queue`).then((r) => r.lines) });
export const useReadyLines = () => useQuery({ queryKey: ['rs', 'ready'], refetchInterval: pollEvery(5_000), queryFn: () => api<{ lines: QueueLine[] }>('/stations/ready').then((r) => r.lines) });
export const useMyShift = (enabled = true) => useQuery({ queryKey: ['rs', 'shift'], enabled, queryFn: () => api<{ shift: Shift | null }>('/shifts/current').then((r) => r.shift) });
export const useShifts = (enabled: boolean) => useQuery({ queryKey: ['rs', 'shifts'], enabled, queryFn: () => api<{ shifts: Shift[] }>('/shifts?limit=30').then((r) => r.shifts) });
export const useInHouse = (search: string, enabled: boolean) =>
  useQuery({ queryKey: ['rs', 'inhouse', search], enabled, queryFn: () => api<{ guests: InHouseGuest[] }>(`/restaurant/in-house${search ? `?search=${encodeURIComponent(search)}` : ''}`).then((r) => r.guests) });
export const useBill = (id: string) => useQuery({ queryKey: ['rs', 'bill', id], queryFn: () => api<Bill & { order: { id: string; reference: string; label: string | null; table: string | null } }>(`/bills/${id}`) });

/** Anything the restaurant does can change orders, tables, queues, tills and today's totals. */
export function useRsMutation<V, R = unknown>(run: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    // Wait for the screen to refresh before the action counts as finished, so the next tap sees the new order
    // (two quick taps on the same dish must raise one line's quantity, not make two lines).
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['rs'] }),
        qc.invalidateQueries({ queryKey: ['menu'] }),
        qc.invalidateQueries({ queryKey: ['daily'] }),
        qc.invalidateQueries({ queryKey: ['bookings'] }),
      ]);
    },
  });
}
